import * as React from "react";

import { useAuth } from "@/providers";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { CollectionRecord } from "@/shared/domain/collections";
import { X } from "@/shared/foundation/icons";

import styles from "./AddToCollection.module.css";
import { CollectionsPicker } from "./CollectionsPicker";
import { CreateForm } from "./CreateForm";
import { SelectedResources } from "./selectedResources";

export type { SelectedResources };

export interface AddToCollectionProps {
  selected: SelectedResources;
  onCancel: () => void;
  onSuccess?: () => void;
  /**
   * Called with `true` as an add starts and `false` once it settles.
   *
   * The writes cannot be cancelled once sent, so the owner should refuse to
   * dismiss the panel while this is `true`; the panel's own close button is
   * disabled for the same span. Without it, a dismissal mid-add drops the
   * outcome: no toast, no error, and the selection is left as it was.
   */
  onPendingChange?: (pending: boolean) => void;
  /**
   * Org whose collections are listed, written to, and created in. Defaults to
   * the signed-in user's active org. Pass it when the selection belongs to an
   * org that can differ from the active one, e.g. a system admin viewing
   * another org's collection.
   */
  orgId?: string;
  /**
   * Collections to leave out of the list, e.g. the collection the selection
   * was made in, where adding its own members would change nothing.
   */
  excludeCollectionIds?: string[];
}

// ── local display helpers (only used within this file) ─────────────────────────

function selectionLabel(selected: SelectedResources): string {
  const count = selected.items.size;
  switch (selected.type) {
    case "Dataset":
      return `${count} ${count === 1 ? "dataset" : "datasets"} selected`;
    case "File":
      return `${count} ${count === 1 ? "file" : "files"} selected`;
    case "Event":
      return `${count} ${count === 1 ? "event" : "events"} selected`;
    case "Session":
      return `${count} ${count === 1 ? "session" : "sessions"} selected`;
  }
}

// ── sub-components ────────────────────────────────────────────────────────────

interface PickerHeaderProps {
  subtitle: string;
  onClose: () => void;
  closeDisabled: boolean;
}

function PickerHeader({ subtitle, onClose, closeDisabled }: PickerHeaderProps) {
  return (
    <div className={styles.header}>
      <div>
        <div className={styles.title}>Add to collection</div>
        <div className={styles.subtitle}>{subtitle}</div>
      </div>
      <button
        className={styles.closeBtn}
        onClick={onClose}
        disabled={closeDisabled}
        aria-label="Close"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export const AddToCollection: React.FC<AddToCollectionProps> = ({
  selected,
  onCancel,
  onSuccess,
  onPendingChange,
  orgId,
  excludeCollectionIds,
}) => {
  const [view, setView] = React.useState<"list" | "create">("list");
  const [isPending, setIsPending] = React.useState(false);
  const handlePendingChange = React.useCallback(
    (pending: boolean) => {
      setIsPending(pending);
      onPendingChange?.(pending);
    },
    [onPendingChange],
  );
  const [chosenRecords, setChosenRecords] = React.useState<
    Map<string, CollectionRecord>
  >(new Map());
  const { currentOrganization } = useAuth();
  const showNotification = useNotification();
  const targetOrgId = orgId ?? currentOrganization?.org_id;

  const subtitle =
    view === "create" ? "Create a new collection" : selectionLabel(selected);

  return (
    <div className={styles.panel}>
      <PickerHeader
        subtitle={subtitle}
        onClose={onCancel}
        closeDisabled={isPending}
      />
      {view === "list" ? (
        <CollectionsPicker
          selected={selected}
          onCancel={onCancel}
          onSuccess={onSuccess}
          onPendingChange={handlePendingChange}
          onCreateNew={() => setView("create")}
          orgId={targetOrgId}
          excludeCollectionIds={excludeCollectionIds}
          chosenRecords={chosenRecords}
          setChosenRecords={setChosenRecords}
        />
      ) : (
        <CreateForm
          onCancel={() => setView("list")}
          onSuccess={() => {
            showNotification("Collection created");
            onSuccess?.();
            onCancel();
          }}
          orgId={targetOrgId ?? ""}
          selected={selected}
        />
      )}
    </div>
  );
};
